import type { IdentifierType } from "./identifiers";
import type { Actor, UserId } from "./types";
import { uuidv7 } from "./ids";

export type SignInMethod =
  "password" | "phone_otp" | "social" | "passkey" | "magic_link" | "oauth" | "unknown";

export interface EventData {
  "user.created": { method: "sign_up" | "admin" | "social" | "import" | "bootstrap" };
  "user.updated": { fields: string[] };
  "user.deleted": { reason?: string };
  "user.signed_in": { method: SignInMethod | string; mfaUsed: boolean; clientId?: string };
  "user.sign_in_failed": { reason: string; emailHash: string };
  "user.signed_out": { scope: "session" | "all" };
  "user.banned": { reason: string };
  "user.unbanned": Record<string, never>;
  "user.roles_changed": { added: string[]; removed: string[] };
  "user.mfa_enabled": { method: string };
  "user.mfa_disabled": { method: string };
  "user.mfa_reset": Record<string, never>;
  "user.password_changed": { viaReset: boolean };
  "user.password_reset_requested": Record<string, never>;
  /** An admin set the password (typed or generated — never logged); the user must change it at next sign-in. */
  "user.password_set_by_admin": { generated: boolean };
  "user.email_verified": Record<string, never>;
  /** `valueHash` = identifierHash(type, value) — raw emails / phone numbers never reach the audit log. */
  "user.identifier_added": { type: IdentifierType; valueHash: string; verified: boolean };
  "user.identifier_removed": { type: IdentifierType; valueHash: string };
  "user.identifier_verified": { type: IdentifierType; valueHash: string; method: "code" | "admin" };
  "user.primary_identifier_changed": { type: IdentifierType; valueHash: string };
  /** User types, groups and application access (docs/access.md). */
  "user.type_changed": { from: string; to: string };
  "user.groups_changed": { added: string[]; removed: string[] };
  /** Root account lifecycle (docs/root-account.md). The password itself never appears in events. */
  "root.seeded": Record<string, never>;
  "root.initial_password_viewed": { via: "cli" | "bootstrap" };
  "root.password_reset": { via: "cli" | "bootstrap" };
  "root.disabled": Record<string, never>;
  "root.enabled": Record<string, never>;
  "session.revoked": { count: number };
  "client.registered": { clientId: string };
  "client.updated": { clientId: string; fields: string[] };
  "client.secret_rotated": { clientId: string };
  "client.disabled": { clientId: string };
  "client.enabled": { clientId: string };
  "settings.updated": { key: string; fromVersion: number; toVersion: number };
  "role.created": { roleId: string };
  "role.updated": { roleId: string };
  "role.deleted": { roleId: string };
  "group.created": { groupId: string };
  "group.updated": { groupId: string; fields: string[] };
  "group.deleted": { groupId: string; members: number };
  "user_type.created": { userTypeId: string };
  "user_type.updated": { userTypeId: string; fields: string[] };
  "user_type.deleted": { userTypeId: string };
  "data.exported": { scope: string; jobId: string };
  "data.imported": { jobId: string; counts: Record<string, number> };
}

export type EventType = keyof EventData;

export const EVENT_TYPES = [
  "user.created",
  "user.updated",
  "user.deleted",
  "user.signed_in",
  "user.sign_in_failed",
  "user.signed_out",
  "user.banned",
  "user.unbanned",
  "user.roles_changed",
  "user.mfa_enabled",
  "user.mfa_disabled",
  "user.mfa_reset",
  "user.password_changed",
  "user.password_reset_requested",
  "user.password_set_by_admin",
  "user.email_verified",
  "user.identifier_added",
  "user.identifier_removed",
  "user.identifier_verified",
  "user.primary_identifier_changed",
  "user.type_changed",
  "user.groups_changed",
  "root.seeded",
  "root.initial_password_viewed",
  "root.password_reset",
  "root.disabled",
  "root.enabled",
  "session.revoked",
  "client.registered",
  "client.updated",
  "client.secret_rotated",
  "client.disabled",
  "client.enabled",
  "settings.updated",
  "role.created",
  "role.updated",
  "role.deleted",
  "group.created",
  "group.updated",
  "group.deleted",
  "user_type.created",
  "user_type.updated",
  "user_type.deleted",
  "data.exported",
  "data.imported",
] as const satisfies readonly EventType[];

export interface DomainEvent<T extends EventType = EventType> {
  id: string;
  type: T;
  version: 1;
  occurredAt: string; // ISO UTC
  actor: Actor;
  subjectUserId?: UserId;
  data: EventData[T];
}

/** Any concrete event (discriminated union over `type`). */
export type AnyDomainEvent = { [K in EventType]: DomainEvent<K> }[EventType];

export const SYSTEM_ACTOR: Actor = { type: "system", id: "system" };

export function createEvent<T extends EventType>(
  type: T,
  init: { actor: Actor; subjectUserId?: UserId; data: EventData[T]; occurredAt?: Date; id?: string },
): DomainEvent<T> {
  const event: DomainEvent<T> = {
    id: init.id ?? uuidv7(),
    type,
    version: 1,
    occurredAt: (init.occurredAt ?? new Date()).toISOString(),
    actor: init.actor,
    data: init.data,
  };
  if (init.subjectUserId !== undefined) event.subjectUserId = init.subjectUserId;
  return event;
}
